import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import { Receipt } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./components/ui/sheet";
import { WalletProvider, useWallet } from "./lib/wallet";
import { useDeals, useDeal } from "./lib/queries";
import { Background } from "./components/terminal/Background";
import { Header } from "./components/terminal/Header";
import { SimBanner } from "./components/terminal/SimBanner";
import { IconRail, MobileNav } from "./components/terminal/IconRail";
import { DealBoard } from "./components/terminal/DealBoard";
import { DealDetail } from "./components/terminal/DealDetail";
import { EscrowTicket } from "./components/terminal/EscrowTicket";
import { ProposeDeal } from "./components/terminal/ProposeDeal";
import { SettledTape } from "./components/terminal/SettledTape";
import { Simulator } from "./components/terminal/Simulator";
import { TokenScanner } from "./components/terminal/TokenScanner";
import { Docs } from "./components/terminal/Docs";
import { SectionInfo } from "./components/terminal/SectionInfo";
import { useCounterpartyAlerts } from "./hooks/useCounterpartyAlerts";
import { ShareProvider } from "./components/terminal/ShareProvider";

const Center = ({ view, selectedId, select }) => {
  const w = useWallet();
  const open = useDeals("open");
  const mine = useDeals("all", w.address);
  const { data: selected } = useDeal(selectedId);

  if (view === "board") return (
    <>
      <DealBoard deals={open.data} isLoading={open.isLoading} isError={open.isError} onRetry={open.refetch} selectedId={selectedId} onSelect={select} />
      <DealDetail deal={selected} />
    </>
  );
  if (view === "mine") return (
    <>
      <DealBoard title="My escrows" deals={w.connected ? mine.data : []} isLoading={w.connected && mine.isLoading} isError={w.connected && mine.isError} onRetry={mine.refetch} selectedId={selectedId} onSelect={select}
        emptyText={w.connected ? "No escrows for this wallet yet. Fund a side on the Deal Board or post a listing." : "Connect a wallet to see the escrows you are part of."} />
      <DealDetail deal={selected} />
    </>
  );
  if (view === "tape") return <SettledTape onSelect={select} />;
  if (view === "simulator") return <Simulator />;
  if (view === "trust") return <TokenScanner />;
  return <Docs />;
};

const TicketPanel = ({ ticketTab, setTicketTab, selected, onCreated }) => (
  <div className="flex flex-col h-full min-h-0">
    <div className="flex border-b border-line shrink-0">
      {[["fund", "Escrow ticket"], ["propose", "Propose"]].map(([id, l]) => (
        <button key={id} onClick={() => setTicketTab(id)} data-testid={`escrow-tab-${id}`}
          className={`flex-1 h-11 font-heading text-[13px] font-semibold uppercase tracking-wider transition-colors relative ${ticketTab === id ? "text-white" : "text-slate-500 hover:text-slate-200"}`}>
          {l}{ticketTab === id && <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-violet" />}
        </button>
      ))}
    </div>
    <div className="flex-1 min-h-0">
      {ticketTab === "fund" ? <EscrowTicket deal={selected} /> : <ProposeDeal onCreated={onCreated} />}
    </div>
  </div>
);

const Terminal = () => {
  const [view, setViewState] = useState("board");
  const [collapsed, setCollapsed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [ticketTab, setTicketTab] = useState("fund");
  const [sheetOpen, setSheetOpen] = useState(false);
  const { data: selected } = useDeal(selectedId);
  const alerts = useCounterpartyAlerts();

  const isMobile = () => typeof window !== "undefined" && window.innerWidth < 1024;
  const setView = (v) => { setViewState(v); setSheetOpen(false); };

  // Open a shared invite link (?deal=X-XXXX) directly into detail + ticket.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("deal");
    if (id) { setSelectedId(id); setTicketTab("fund"); setViewState("board"); if (isMobile()) setSheetOpen(true); }
  }, []);

  const select = (id) => { setSelectedId(id); setTicketTab("fund"); if (isMobile()) setSheetOpen(true); };
  const onCreated = (d) => { setSelectedId(d.id); setTicketTab("fund"); setViewState("board"); setSheetOpen(false); };

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col bg-ink text-white relative">
      <Background />
      <div className="relative z-10 flex flex-col h-full min-h-0">
        <Header alerts={alerts} />
        <SimBanner />
        <div className="flex flex-1 min-h-0">
          <IconRail view={view} setView={setView} collapsed={collapsed} setCollapsed={setCollapsed} />
          <main className="flex-1 min-w-0 flex flex-col gap-3 p-3 pb-24 lg:pb-3 overflow-y-auto lg:overflow-hidden animate-fade-up" key={view}>
            <SectionInfo view={view} />
            <Center view={view} selectedId={selectedId} select={select} />
          </main>
          <aside className="hidden lg:flex w-[400px] xl:w-[440px] shrink-0 border-l border-line bg-s1 flex-col min-h-0" data-testid="right-rail">
            <TicketPanel ticketTab={ticketTab} setTicketTab={setTicketTab} selected={selected} onCreated={onCreated} />
          </aside>
        </div>
      </div>

      {/* Mobile navigation + escrow ticket access */}
      <MobileNav view={view} setView={setView} />
      <button onClick={() => { setTicketTab("fund"); setSheetOpen(true); }} data-testid="mobile-ticket-fab" aria-label="Open escrow ticket and propose"
        className="lg:hidden fixed bottom-20 right-4 z-40 h-12 w-12 rounded-full btn-primary p-0 shadow-[0_8px_30px_-6px_rgba(192,38,245,0.8)]">
        <Receipt size={20} />
      </button>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="lg:hidden bg-s1 border-line text-white p-0 h-[88vh] flex flex-col" data-testid="mobile-ticket-sheet">
          <SheetHeader className="px-4 py-3 border-b border-line text-left shrink-0">
            <SheetTitle className="font-heading text-sm uppercase tracking-wider text-slate-200">Escrow ticket</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            <TicketPanel ticketTab={ticketTab} setTicketTab={setTicketTab} selected={selected} onCreated={onCreated} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const TOAST_OPTIONS = { className: "!bg-s2 !border-line2 !text-white !font-sans" };

export default function App() {
  return (
    <WalletProvider>
      <ShareProvider>
        <Terminal />
      </ShareProvider>
      <Toaster theme="dark" position="bottom-right" toastOptions={TOAST_OPTIONS} />
    </WalletProvider>
  );
}
